import React, { useState } from 'react';
import { Shield, FileText, XCircle, X } from 'lucide-react';

// Policy content
const POLICIES = {
  privacy: {
    title: 'Terms & Conditions',
    lastUpdated: '01-04-2026 15:54:24',
    content: `Welcome to MAKS SPORTS INFRA & TECH LLP. These Terms and Conditions, together with our Privacy Policy and other applicable terms ("Terms"), form a binding agreement between you ("you" or "your") and us ("Website Owner", "we", "us", or "our") regarding your use of our website, goods, and services ("Services").

By accessing our website or using our Services, you confirm that you have read and accepted these Terms and our Privacy Policy.

We may update these Terms at any time without prior notice. Please review them regularly to stay informed. Your continued use of our website or Services means you accept any changes.

**Account Information:** You agree to provide accurate and complete information during registration and afterwards. You are responsible for all actions taken through your account.

**No Warranty:** We and third parties do not guarantee the accuracy, timeliness, performance, completeness, or suitability of information and materials on this website or through our Services. Such information may contain errors, and we expressly exclude liability for inaccuracies to the fullest extent permitted by law.

**Your Responsibility:** Use our Services and website at your own risk and discretion. Assess independently whether our Services meet your needs. All website content and Services are proprietary; you have no rights to claim intellectual property, title, or interest. Unauthorized use may result in action under these Terms or applicable laws.

**Charges & Lawful Use:** You agree to pay applicable charges for Services. Do not use the website or Services for unlawful purposes or in violation of these Terms or any applicable laws. Our website and Services may include links to third-party websites.

**Third-Party Links:** When accessing third-party links, you are subject to their terms of use, privacy policy, and other policies.

**Transactions & Refunds:** Initiating a transaction for Services forms a legally binding contract. You may claim a refund if we cannot provide the Service. Refund timelines depend on the specific Service or our policies. Claims outside the stipulated period are ineligible for refund.

**Force Majeure:** Neither party is liable for failure to perform obligations if prevented or delayed by a force majeure event.

**Governing Law:** These Terms and related disputes are governed by the laws of India. All disputes are subject to the exclusive jurisdiction of courts in Mumbai and Thane.

**Contact:** For concerns or communications regarding these Terms, please use the contact information provided on our website.

**Note:** Please review these terms regularly. Continued use of our website and Services means you accept any changes.`
  },
  terms: {
    title: 'Terms of Service',
    lastUpdated: '01-04-2026 15:54:24',
    content: `Welcome to MAKS SPORTS INFRA & TECH LLP. These Terms and Conditions, together with our Privacy Policy and other applicable terms ("Terms"), form a binding agreement between you ("you" or "your") and us ("Website Owner", "we", "us", or "our") regarding your use of our website, goods, and services ("Services").

By accessing our website or using our Services, you confirm that you have read and accepted these Terms and our Privacy Policy.

We may update these Terms at any time without prior notice. Please review them regularly to stay informed. Your continued use of our website or Services means you accept any changes.

**Account Information:** You agree to provide accurate and complete information during registration and afterwards. You are responsible for all actions taken through your account.

**No Warranty:** We and third parties do not guarantee the accuracy, timeliness, performance, completeness, or suitability of information and materials on this website or through our Services. Such information may contain errors, and we expressly exclude liability for inaccuracies to the fullest extent permitted by law.

**Your Responsibility:** Use our Services and website at your own risk and discretion. Assess independently whether our Services meet your needs. All website content and Services are proprietary; you have no rights to claim intellectual property, title, or interest. Unauthorized use may result in action under these Terms or applicable laws.

**Charges & Lawful Use:** You agree to pay applicable charges for Services. Do not use the website or Services for unlawful purposes or in violation of these Terms or any applicable laws. Our website and Services may include links to third-party websites.

**Third-Party Links:** When accessing third-party links, you are subject to their terms of use, privacy policy, and other policies.

**Transactions & Refunds:** Initiating a transaction for Services forms a legally binding contract. You may claim a refund if we cannot provide the Service. Refund timelines depend on the specific Service or our policies. Claims outside the stipulated period are ineligible for refund.

**Force Majeure:** Neither party is liable for failure to perform obligations if prevented or delayed by a force majeure event.

**Governing Law:** These Terms and related disputes are governed by the laws of India. All disputes are subject to the exclusive jurisdiction of courts in Mumbai and Thane.

**Contact:** For concerns or communications regarding these Terms, please use the contact information provided on our website.

**Note:** Please review these terms regularly. Continued use of our website and Services means you accept any changes.`
  },
  cancellation: {
    title: 'Cancellation Policy',
    lastUpdated: '01-04-2026 15:54:24',
    content: `**MAKS SPORTS INFRA & TECH LLP - Cancellation Policy**

**No Cancellation Policy**

Once a booking is confirmed and payment is made, cancellations are NOT permitted. Please ensure you have confirmed your availability before making a booking.

**Key Points:**

1. **No Refunds:** All bookings are final. No refunds will be issued for any reason, including but not limited to:
   - Change of plans
   - Weather conditions
   - Personal emergencies
   - Scheduling conflicts

2. **Rescheduling:** Rescheduling of bookings is not available. If you cannot attend your booked slot, you forfeit the booking.

3. **No-Show Policy:** If you fail to arrive for your booked slot, the booking will be marked as completed and no refund or credit will be provided.

4. **Technical Issues:** In the rare event of technical issues on our end that prevent you from using the booked facility, we will work with you to find a suitable alternative or provide a credit for future use.

5. **Facility Closure:** If the facility is closed due to unforeseen circumstances (maintenance, natural disasters, etc.), we will provide a credit for future bookings.

**Contact Us:**

For any questions regarding this policy, please contact us through our website or at the venue directly.

**Note:** By completing a booking, you acknowledge that you have read and agree to this Cancellation Policy.`
  }
};

// Policy Modal Component
const PolicyModal = ({ isOpen, onClose, policyType }) => {
  if (!isOpen) return null;
  
  const policy = POLICIES[policyType];
  if (!policy) return null;

  // Parse markdown-style bold text
  const renderContent = (text) => {
    return text.split('\n').map((line, idx) => {
      // Handle bold text
      const parts = line.split(/\*\*(.*?)\*\*/g);
      const rendered = parts.map((part, i) => 
        i % 2 === 1 ? <strong key={i} className="font-semibold text-slate-800">{part}</strong> : part
      );
      
      return (
        <p key={idx} className={`${line.trim() === '' ? 'h-4' : 'mb-2'}`}>
          {rendered}
        </p>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-violet-500 to-purple-600 text-white">
          <div>
            <h2 className="text-lg font-bold">{policy.title}</h2>
            <p className="text-xs text-violet-200">Last updated: {policy.lastUpdated}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 text-sm text-slate-600 leading-relaxed">
          {renderContent(policy.content)}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t bg-slate-50">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold rounded-xl hover:from-violet-600 hover:to-purple-700 transition-colors"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

// Footer Component
export default function CustomerFooter() {
  const [activePolicy, setActivePolicy] = useState(null);

  return (
    <>
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="px-4 py-4">
          {/* Policy Links */}
          <div className="flex items-center justify-center gap-4 mb-3">
            <button
              onClick={() => setActivePolicy('privacy')}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-violet-600 transition-colors"
              data-testid="footer-privacy-link"
            >
              <Shield className="w-4 h-4" />
              Privacy
            </button>
            <span className="text-slate-300">|</span>
            <button
              onClick={() => setActivePolicy('terms')}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-violet-600 transition-colors"
              data-testid="footer-terms-link"
            >
              <FileText className="w-4 h-4" />
              Terms
            </button>
            <span className="text-slate-300">|</span>
            <button
              onClick={() => setActivePolicy('cancellation')}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-violet-600 transition-colors"
              data-testid="footer-cancellation-link"
            >
              <XCircle className="w-4 h-4" />
              Cancellation Policy
            </button>
          </div>
          
          {/* Copyright */}
          <div className="text-center">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} MAKS SPORTS INFRA & TECH LLP. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Policy Modal */}
      <PolicyModal
        isOpen={activePolicy !== null}
        onClose={() => setActivePolicy(null)}
        policyType={activePolicy}
      />
    </>
  );
}
